import { FunctionComponent, ReactNode, useEffect, useMemo } from 'react';
import {
  chooseColor,
  formattedCurrency,
  withStyles,
  capitalizeFirstLetterOfWords,
  semiMonthlyFrequencyLabel,
  frequencyLabel,
  InteracWordmark,
  ELinxConnectionMethod,
} from 'vopay';
import { Trans, useTranslation } from 'react-i18next';
import { format, parseISO, isValid } from 'date-fns';
import { useLayout } from '@/providers/Layout.provider';
import { usePaylink } from '@/providers/Paylink.provider';
import { useToken } from '@/providers/Token.provider';
import { useBank } from '@/providers/Bank.provider';
import Complete from './Complete/Complete';
import { useConvenienceFeesProvider } from '@/providers/ConvenienceFees.provider';
import { useLocation } from 'react-router-dom';
import RestrictionsTooltip from '@/components/UI/RestrictionsTooltip';
import styles from './Thankyou.module.scss';
import { useFlexiblePayment } from '@/providers/FlexiblePayment.provider';
import { ConvenienceFeeBreakdown } from 'UI';
import { getBankingRailLabel } from '@/utils/bankingRailMapping';

const classes = withStyles(styles);

type LocationState = {
  paymentMethod: string;
  paymentMethodType?: ELinxConnectionMethod | '';
  province?: string;
  country?: string;

  connectionType?: 'manual' | 'online';
  bankId?: string;
  accountNumber?: string;
  institutionNumber?: string;
  branchTransitNumber?: string;
  routingNumber?: string;

  cardNumber?: string;
  cardHolderName?: string;
  cardBrand?: string;
  cardExpiryYear?: string;
  cardExpiryMonth?: string;
  address?: string;
  address2?: string;
  city?: string;
  postalCode?: string;

  currency?: string;

  emailConnectionType?: 'email' | 'phone';
  email?: string;
  phone?: string;

  isExistingConnection?: boolean;
};

const Thankyou: FunctionComponent = () => {
  const { setLayoutInfo } = useLayout();
  const { paylink } = usePaylink();
  const { token } = useToken();
  const { state } = useLocation<LocationState>();
  const { getProcessingFee, convenienceFees, showFees } = useConvenienceFeesProvider();
  const { t } = useTranslation('translations', { keyPrefix: 'Thankyou' });
  const { isFlexiblePayment } = useFlexiblePayment();
  const { BankAccountRail } = useBank();

  useEffect(() => {
    setLayoutInfo(
      chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'curve',
      '',
      80,
      20,
      0,
      false,
      true,
    );
  }, []);

  // Determine transaction type based on flow
  const transactionType = useMemo(() => {
    if (paylink.PaymentType === 'withdraw') {
      return 'received';
    } else if (paylink.PaymentType === 'fund') {
      return 'sent';
    }
    return 'sent';
  }, [paylink.PaymentType]);

  // Determine if amounts should be shown; 'beneficiary' means it's just a connection, not a payment or collection
  const hasTransaction = useMemo(() => paylink.Type !== 'beneficiary' && paylink.Type !== '', [paylink.Type]);

  const pageContent = useMemo(() => {
    const isExistingConnection = state?.isExistingConnection || false;
    const type = paylink.Type;

    // econnect scenario (beneficiary)
    if (type === 'beneficiary') {
      return {
        title: t('paymentMethodConnected', { defaultValue: 'Payment Method Connected' }),
        subText: t('paymentDetailsSaved', { defaultValue: 'Your payment details have been securely saved' }),
        transactionText: undefined,
      };
    }

    // epay scenario (transaction with fund payment type)
    if ((type === 'transaction' || type === 'scheduled') && paylink.PaymentType === 'withdraw') {
      if (isExistingConnection) {
        return {
          title: t('paymentMethodConfirmed', { defaultValue: 'Payment Method Confirmed' }),
          subText: t('savedPaymentMethodChosen', {
            defaultValue: "You've chosen a saved payment method for this payment",
          }),
          transactionText: t('upcomingAmount', { defaultValue: 'Upcoming Amount' }),
        };
      } else {
        return {
          title: t('paymentMethodConnected', { defaultValue: 'Payment Method Connected' }),
          subText: t('paymentDetailsSaved', {
            defaultValue: 'Your payment details have been securely saved',
          }),
          transactionText: t('upcomingAmount', { defaultValue: 'Upcoming Amount' }),
        };
      }
    }

    // eflex scenario (flexible payment)
    if (isFlexiblePayment) {
      if (isExistingConnection) {
        return {
          title: t('paymentMethodConfirmed', { defaultValue: 'Payment Method Confirmed' }),
          subText: t('savedPaymentMethodChosen', {
            defaultValue: "You've chosen a saved payment method for this payment",
          }),
          transactionText: t('paymentAmount', { defaultValue: 'Payment Amount' }),
        };
      } else {
        return {
          title: t('paymentMethodConnected', { defaultValue: 'Payment Method Connected' }),
          subText: t('paymentDetailsSaved', {
            defaultValue: 'Your payment details have been securely saved',
          }),
          transactionText: t('paymentAmount', { defaultValue: 'Payment Amount' }),
        };
      }
    }
    // ecollect scenario (transaction with withdraw payment type)
    if ((type === 'transaction' || type === 'scheduled') && paylink.PaymentType === 'fund') {
      if (isExistingConnection) {
        return {
          title: t('paymentMethodConfirmed', { defaultValue: 'Payment Method Confirmed' }),
          subText: t('savedPaymentMethodChosen', {
            defaultValue: "You've chosen a saved payment method for this payment",
          }),
          transactionText: t('paymentAmount', { defaultValue: 'Payment Amount' }),
        };
      } else {
        return {
          title: t('paymentMethodConnected', { defaultValue: 'Payment Method Connected' }),
          subText: t('paymentDetailsSaved', {
            defaultValue: 'Your payment details have been securely saved',
          }),
          transactionText: t('paymentAmount', { defaultValue: 'Payment Amount' }),
        };
      }
    }

    // Default fallback for other scenarios
    return {
      title: hasTransaction ? t('transactionSuccessful') : t('connectionSuccessful'),
      subText: undefined,
      transactionText: undefined,
    };
  }, [paylink.Type, paylink.PaymentType, state?.isExistingConnection, isFlexiblePayment, hasTransaction, t]);

  // Calculate fee breakdown details
  const amountBreakdown = useMemo(() => {
    let breakdown = {
      baseAmount: 0,
      currency: 'CAD',
      totalAmount: 0,
      showFees: false,
      hasProcessingFee: false,
    };

    if (!paylink) return breakdown;

    const currency = paylink.Currency || 'CAD';
    breakdown.baseAmount = parseFloat(paylink.Amount || '0');
    breakdown.currency = currency;

    if (!state?.paymentMethodType || !showFees) return breakdown;

    const feeData = getProcessingFee({
      paymentMethod: state.paymentMethodType,
      province: state.province,
      bankingPaymentRail: BankAccountRail,
    });

    if (feeData === null) return breakdown;

    breakdown.showFees = true;
    const totalAmount = feeData.totalAmountNumeric;
    breakdown.totalAmount = totalAmount;
    breakdown.hasProcessingFee = feeData.processingFeeNumeric !== 0;

    return breakdown;
  }, [showFees, state?.paymentMethodType, paylink, token?.Country, transactionType]);

  // Get amount from paylink object with convenience fees
  const amount = useMemo(() => {
    if (!paylink || !hasTransaction) return '';

    if (!amountBreakdown.showFees) return `${paylink.Currency} ${formattedCurrency(paylink.Amount)}`;

    if (amountBreakdown.baseAmount === 0) return '';

    const formattedAmount = formattedCurrency(amountBreakdown.totalAmount.toString());

    return `${amountBreakdown.currency} ${formattedAmount}`;
  }, [amountBreakdown, paylink, hasTransaction]);

  // Determine if this was a combined credit/debit card flow
  const isCombinedCardFlow = useMemo(() => {
    const paymentSelectionMethod = token?.PaymentSelectionMethod || '';
    const hasCredit = paymentSelectionMethod === 'any' || paymentSelectionMethod.includes('credit');
    const hasDebit = paymentSelectionMethod === 'any' || paymentSelectionMethod.includes('debitcard');
    const cardTypeValidationDisabled = token?.CardTypeValidation === false;

    return hasCredit && hasDebit && cardTypeValidationDisabled && state?.paymentMethodType === 'credit';
  }, [token?.PaymentSelectionMethod, token?.CardTypeValidation, state?.paymentMethodType]);

  const transactionDetails = useMemo(() => {
    if (isFlexiblePayment) return [];

    const methodNames: Record<ELinxConnectionMethod, ReactNode> = {
      credit: isCombinedCardFlow ? t('creditDebitCard') : t('creditCard'),
      debitcard: t('debitCard'),
      bank: t('bankAccount'),
      email: <InteracWordmark />,
      googlepay: t('googlePay'),
      applepay: t('applePay'),
      paypal: t('payPal'),
      venmo: t('venmo'),
      zelle: t('zelle'),
    };

    const rows = [];

    // Initial Requested Amount
    if (amountBreakdown.showFees && paylink.Type !== 'beneficiary') {
      const formattedBaseAmount = formattedCurrency(amountBreakdown.baseAmount.toString());

      rows.push({
        label: t('initialRequestedAmount'),
        value: `${amountBreakdown.currency} ${formattedBaseAmount}`,
      });
    }

    if (state?.paymentMethodType) {
      // Check if it's a bank account with multiple rails (paylink only, not beneficiary)
      const isPaylinkBankWithMultipleRails =
        state?.paymentMethodType === 'bank' &&
        paylink.Type !== 'beneficiary' &&
        BankAccountRail &&
        Array.isArray(paylink.BankAccountRail) &&
        paylink.BankAccountRail.length > 1;

      let paymentMethodLabel = methodNames[state?.paymentMethodType] || state?.paymentMethodType;

      // Add rail suffix for bank accounts with multiple rails in paylink
      if (isPaylinkBankWithMultipleRails && BankAccountRail) {
        const railLabel = getBankingRailLabel(BankAccountRail);
        paymentMethodLabel = `Bank Account (${railLabel})`;
      }

      rows.push({
        label: t('paymentMethod'),
        value: paymentMethodLabel,
      });
    }

    // Payment Method (always show for flows 1-4)
    if (state?.paymentMethod) {
      rows.push({
        label: t('paymentMethodDetails'),
        value: state?.paymentMethod,
      });
    }

    // Payment Method Fee (if there are fees)
    if (amountBreakdown.showFees && state.paymentMethodType && paylink.Type !== 'beneficiary') {
      const feeAmount = amountBreakdown.totalAmount - amountBreakdown.baseAmount;
      const isFree = feeAmount === 0;

      // Check if it's a bank account with multiple rails (paylink only)
      const isPaylinkBankWithMultipleRails =
        state?.paymentMethodType === 'bank' &&
        BankAccountRail &&
        Array.isArray(paylink.BankAccountRail) &&
        paylink.BankAccountRail.length > 1;

      let feeMethodLabel = methodNames[state?.paymentMethodType || ''] || state?.paymentMethodType;

      // Add rail suffix for bank accounts with multiple rails in paylink
      if (isPaylinkBankWithMultipleRails && BankAccountRail) {
        const railLabel = getBankingRailLabel(BankAccountRail);
        feeMethodLabel = `Bank Account (${railLabel})`;
      }

      rows.push({
        label: (
          <div className={classes('feeLabelField')}>
            <span>
              {
                // need <Trans /> for the <InteracWordmark />
              }
              <Trans ns="translations" i18nKey="Thankyou.paymentTypeFee">
                {feeMethodLabel}
              </Trans>
            </span>
            <RestrictionsTooltip
              restrictions={convenienceFees?.Restrictions || []}
              country={state?.country || token?.Country}
            />
          </div>
        ),
        value: (
          <div className={classes('feeInfoContainer')}>
            <span className={classes('feeInfoAmount')}>
              {isFree ? t('free') : `${amountBreakdown.currency} ${formattedCurrency(feeAmount.toString())}`}
            </span>

            {!isFree && state.paymentMethodType && amountBreakdown.hasProcessingFee && (
              <span className={classes('feeInfoText')}>
                (
                <ConvenienceFeeBreakdown
                  className={classes('feeInfoConvenienceFees')}
                  connectionMethod={state.paymentMethodType}
                  showDeliveryTime={false}
                />
                )
              </span>
            )}
          </div>
        ),
      });

      const formattedTotalAmount = formattedCurrency(amountBreakdown.totalAmount.toString());

      rows.push({
        label: t('totalSentAmount'),
        value: `${amountBreakdown.currency} ${formattedTotalAmount}`,
      });
    }

    return rows;
  }, [
    amountBreakdown,
    state?.paymentMethod,
    state?.paymentMethodType,
    t,
    convenienceFees?.Restrictions,
    token?.Country,
    classes,
    isCombinedCardFlow,
    paylink.Type,
    paylink.BankAccountRail,
    BankAccountRail,
  ]);

  const scheduleDetails = useMemo(() => {
    const shouldShowDetails = paylink.Type === 'scheduled';
    if (!shouldShowDetails || !paylink || isFlexiblePayment) return [];

    const details = [];

    // Schedule Type
    if (paylink.Frequency) {
      details.push({
        label: t('scheduleType'),
        value: capitalizeFirstLetterOfWords(paylink.Frequency),
      });
    }

    // Start Date
    if (paylink.ScheduleStartDate) {
      // Parse ISO date string properly to avoid timezone issues
      const startDate = parseISO(paylink.ScheduleStartDate);
      // Ensure the date is valid before formatting
      const formattedStartDate = isValid(startDate) ? format(startDate, 'MMM d, yyyy') : paylink.ScheduleStartDate;

      details.push({
        label: t('startDate'),
        value: formattedStartDate,
      });
    }

    // End Date
    if (paylink.ScheduleEndDate) {
      // Parse ISO date string properly to avoid timezone issues
      const endDate = parseISO(paylink.ScheduleEndDate);
      // Ensure the date is valid before formatting
      const formattedEndDate = isValid(endDate) ? format(endDate, 'MMM d, yyyy') : paylink.ScheduleEndDate;

      details.push({
        label: t('endDate'),
        value: formattedEndDate,
      });
    }

    if (paylink.EndingAfterPayments) {
      details.push({
        label: t('remainingPayments'),
        value: paylink.EndingAfterPayments,
      });
    }

    if (paylink.NameOfFrequency) {
      details.push({
        label: t('frequency'),
        value: frequencyLabel(paylink.NameOfFrequency),
      });
    }

    if (paylink.SemiMonthlyFrequencyType) {
      details.push({
        label: t('semiMonthlyFrequency'),
        value: semiMonthlyFrequencyLabel(paylink.SemiMonthlyFrequencyType),
      });
    }

    return details;
  }, [paylink.Type, paylink, t]);

  return (
    <Complete
      title={pageContent.title}
      subText={pageContent.subText}
      amount={amount}
      username={paylink.SenderName}
      transactionType={transactionType}
      transactionText={pageContent.transactionText}
      showAmounts={hasTransaction && !isFlexiblePayment} // Hide amounts for flexible payment temporary until we build for flexible payment
      details={[...scheduleDetails, ...transactionDetails]}
    >
      {amountBreakdown.showFees &&
        state.paymentMethodType &&
        (paylink.Type === 'beneficiary' || paylink.Type === '') && (
          <div className={classes('convenienceFeeBreakdownContainer')}>
            {t('noChargesMessage', {
              defaultValue:
                'No charges were made. Fees will apply only if this payment method is used in a future transaction.',
            })}
          </div>
        )}
    </Complete>
  );
};

export default Thankyou;
